import { useEffect, useRef } from "react";
import * as THREE from "three";

interface StarfieldProps {
	numStars?: number;
	speed?: number;
	starSize?: number;
	starColor?: string;
	className?: string;
}

export function Starfield({
	numStars = 1000,
	speed = 0.05,
	starSize = 0.5,
	starColor = "#bcbbb6",
	className = "",
}: StarfieldProps) {
	const containerRef = useRef<HTMLDivElement>(null);
	const sceneRef = useRef<THREE.Scene | null>(null);
	const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
	const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
	const starsRef = useRef<THREE.Points | null>(null);
	const animationIdRef = useRef<number | null>(null);

	useEffect(() => {
		if (!containerRef.current) return;

		// Setup scene
		const scene = new THREE.Scene();
		sceneRef.current = scene;

		// Setup camera
		const camera = new THREE.PerspectiveCamera(
			75,
			containerRef.current.clientWidth / containerRef.current.clientHeight,
			0.1,
			1000,
		);
		camera.position.z = 5;
		cameraRef.current = camera;

		// Setup renderer
		const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
		renderer.setSize(
			containerRef.current.clientWidth,
			containerRef.current.clientHeight,
		);
		renderer.setPixelRatio(window.devicePixelRatio);
		containerRef.current.appendChild(renderer.domElement);
		rendererRef.current = renderer;

		// Create circular gradient texture for stars
		const canvas = document.createElement("canvas");
		canvas.width = 32;
		canvas.height = 32;
		const ctx = canvas.getContext("2d");
		if (ctx) {
			const gradient = ctx.createRadialGradient(16, 16, 0, 16, 16, 16);
			gradient.addColorStop(0, "rgba(255, 255, 255, 0.5)");
			gradient.addColorStop(0.2, "rgba(255, 255, 255, 0.4)");
			gradient.addColorStop(0.4, "rgba(255, 255, 255, 0.4)");
			gradient.addColorStop(0.7, "rgba(255, 255, 255, 0.2)");
			gradient.addColorStop(1, "rgba(255, 255, 255, 0)");
			ctx.fillStyle = gradient;
			ctx.fillRect(0, 0, 32, 32);
		}
		const texture = new THREE.CanvasTexture(canvas);

		// Create stars
		const geometry = new THREE.BufferGeometry();
		const positions = new Float32Array(numStars * 3);
		const velocities = new Float32Array(numStars);

		for (let i = 0; i < numStars; i++) {
			const i3 = i * 3;
			// Random position in a cube
			positions[i3] = (Math.random() - 0.5) * 100;
			positions[i3 + 1] = (Math.random() - 0.5) * 100;
			positions[i3 + 2] = (Math.random() - 0.5) * 100;

			// Random velocity for each star
			velocities[i] = Math.random() * 0.5 + 0.5;
		}

		geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
		geometry.setAttribute("velocity", new THREE.BufferAttribute(velocities, 1));

		const material = new THREE.PointsMaterial({
			color: starColor,
			size: starSize,
			transparent: true,
			opacity: 0.8,
			sizeAttenuation: true,
			map: texture,
			blending: THREE.AdditiveBlending,
			depthWrite: false,
		});

		const stars = new THREE.Points(geometry, material);
		scene.add(stars);
		starsRef.current = stars;

		// Animation loop
		const animate = () => {
			if (!starsRef.current) return;

			const positions = starsRef.current.geometry.attributes.position!
				.array as Float32Array;
			const velocities = starsRef.current.geometry.attributes.velocity!
				.array as Float32Array;

			// Move stars towards camera (increase z position)
			for (let i = 0; i < numStars; i++) {
				const i3 = i * 3;
				positions[i3 + 2]! += velocities[i]! * speed;

				// Reset star if it passes the camera
				if (positions[i3 + 2]! > 50) {
					positions[i3] = (Math.random() - 0.5) * 100;
					positions[i3 + 1] = (Math.random() - 0.5) * 100;
					positions[i3 + 2] = -50;
				}
			}

			starsRef.current.geometry.attributes.position!.needsUpdate = true;

			if (rendererRef.current && cameraRef.current && sceneRef.current) {
				rendererRef.current.render(sceneRef.current, cameraRef.current);
			}

			animationIdRef.current = requestAnimationFrame(animate);
		};

		animate();

		// Handle window resize
		const handleResize = () => {
			if (!containerRef.current || !cameraRef.current || !rendererRef.current)
				return;

			const width = containerRef.current.clientWidth;
			const height = containerRef.current.clientHeight;

			cameraRef.current.aspect = width / height;
			cameraRef.current.updateProjectionMatrix();
			rendererRef.current.setSize(width, height);
		};

		window.addEventListener("resize", handleResize);

		// Cleanup
		return () => {
			window.removeEventListener("resize", handleResize);

			if (animationIdRef.current) {
				cancelAnimationFrame(animationIdRef.current);
			}

			if (rendererRef.current && containerRef.current) {
				containerRef.current.removeChild(rendererRef.current.domElement);
				rendererRef.current.dispose();
			}

			if (starsRef.current) {
				starsRef.current.geometry.dispose();
				if (starsRef.current.material instanceof THREE.Material) {
					starsRef.current.material.dispose();
				}
			}
		};
	}, [numStars, speed, starSize, starColor]);

	return (
		<div
			ref={containerRef}
			className={`z-2 absolute top-0 left-0 w-screen h-screen pointer-events-none ${className}`}
		/>
	);
}
